import type { ReactNode } from 'react'

import { useRef } from 'react'

import { useGsap } from '@/hooks/use-gsap'

interface CounterProps {
    value: number
    decimals?: number
    suffix?: string
    prefix?: string
    className?: string
}

/**
 * Counts up to its value the first time it scrolls into view.
 * The finished figure is rendered server-side, so it reads correctly with no JS.
 */
export const Counter = ({
    value,
    decimals = 0,
    suffix = '',
    prefix = '',
    className
}: CounterProps): ReactNode => {
    const output = useRef<HTMLSpanElement | null>(null)

    const scope = useGsap<HTMLSpanElement>(({ gsap, scope: root, reduced }) => {
        const node = output.current

        if (!node || reduced) return

        const counter = { current: 0 }

        // Reset before paint so the figure never flashes its final value first.
        node.textContent = (0).toFixed(decimals)

        gsap.to(counter, {
            current: value,
            duration: 1.8,
            ease: 'power2.out',
            scrollTrigger: { trigger: root, start: 'top 88%', once: true },
            onUpdate: () => {
                node.textContent = counter.current.toFixed(decimals)
            }
        })
    }, [value, decimals])

    return (
        <span ref={scope} className={className}>
            {prefix}

            <span ref={output} className='tabular-nums'>
                {value.toFixed(decimals)}
            </span>

            {suffix}
        </span>
    )
}
